import API from './api.js';
import { getAuthCookie, removeAuthCookie } from '../utils/cookies.js';
import { DEFAULT_PAGE_SIZE } from '../components/treasury/treasury.constants';
import { USER_SUMMARY_PAGE_SIZE } from '../components/dashboard/dashboard.constants';
import { buildAnalyticsPeriodParams } from '../utils/periodParams.js';
import { isCanceledError } from '../utils/httpErrors.js';
import { resetUnread } from './notifications.store.js';
import { resetBreadcrumb } from './breadcrumb.store.js';
import { setSyncStateCatalog } from '../components/common/statusClassification/statusClassification';
import { mapExportResponse, mapExportJobError } from '../utils/exportResponse.js';

class DataController {
    user = null;
    context = null;
    aba = null;
    _fetchUserByTokenPromise = null;
    _syncStateCatalogPromise = null;
    _jiraStatusMapPromise = null;
    static _instance = null;

    /** Shared data layer — one cache + one in-flight user fetch per session. */
    static Instance() {
        if (!DataController._instance) {
            DataController._instance = new DataController();
        }
        return DataController._instance;
    }

    /** Clear cached session (call on logout). */
    static clearSession() {
        const inst = DataController._instance;
        if (!inst) return;
        inst.user = null;
        inst.aba = null;
        inst.context = null;
        inst._fetchUserByTokenPromise = null;
        resetUnread();
        resetBreadcrumb();
        inst._syncStateCatalogPromise = null;
        setSyncStateCatalog(null);
        inst._jiraStatusMapPromise = null;
    }

    /**
     * The sync-state classification catalog
     * (`GET /api/admin/reports/:aba/sync-state-catalog`), fetched ONCE per
     * session and installed into the classification store every status pill
     * colours from (`statusClassification.toneFor`). Never throws: on failure
     * the store stays empty, pills render neutral, and the next call retries.
     *
     * @returns {Promise<{ classes: string[], families: string[], states: object[] } | null>}
     */
    async fetchSyncStateCatalog() {
        if (this._syncStateCatalogPromise) return this._syncStateCatalogPromise;
        this._syncStateCatalogPromise = (async () => {
            try {
                const response = await this.API.requestSyncStateCatalog(this.aba);
                const catalog = response?.data;
                if (!Array.isArray(catalog?.states)) throw new Error('sync-state catalog payload has no states');
                setSyncStateCatalog(catalog);
                return catalog;
            } catch (error) {
                console.error('Error in fetchSyncStateCatalog', error);
                this._syncStateCatalogPromise = null; // let the next screen retry
                return null;
            }
        })();
        return this._syncStateCatalogPromise;
    }

    /**
     * Get the API instance
     * @returns {API}
     */
    get API() {
        return API.Instance();
    }

    async authenticate() {
        try {
            const token = getAuthCookie();
            if (token === "expired") return false;
            if (this.user) return true;
            const user = await this.fetchUserByToken();
            if (user) {
                this.user = user;
                this.aba = user?.access[0];
                return true;
            }
            return false;
        } catch (error) {
            console.error("Error in authenticate", error);
            return false;
        }
    }

    async fetchUserByToken() {
        if (this.user) return this.user;

        if (this._fetchUserByTokenPromise) {
            return this._fetchUserByTokenPromise;
        }

        this._fetchUserByTokenPromise = this._fetchUserByTokenImpl();
        try {
            return await this._fetchUserByTokenPromise;
        } finally {
            this._fetchUserByTokenPromise = null;
        }
    }

    async _fetchUserByTokenImpl() {
        try {
            const token = getAuthCookie();
            if (token === "expired") return {};

            const user = await this.API.requestUser(token);
            if (user === 401) return {};

            if (!user?.data?.access[0]) return {access: false};

            const fi = await this.fetchFI(user?.data?.access[0]);
            const userData = {...user?.data, fi: fi || {}};
            this.user = userData;
            this.aba = user?.data?.access[0];
            return userData;
        } catch (error) {
            console.error("Error in fetchUserByToken", error);
            removeAuthCookie();
            DataController.clearSession();
            window.location.href = "/";
            return {};
        }
    }

    async loginUser(payload) {
        try {
            const response = await this.API.requestAuthenticate(payload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in loginUser", error);
            return {};
        }
    }

    async fetchReport(aba, start, end, grouping, timeFrame) {
        try {
            const response = await this.API.requestReport(aba || this.aba, start, end, grouping, timeFrame);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchReport", error);
            return {};
        }
    }

    async fetchFI(aba) {
        try {
            const response = await this.API.requestFI(aba || this.aba);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchFI", error);
            return {};
        }
    }

    /**
     * Open one Open SSO session and return the whole boot bundle. The single
     * scope-gated, audited entry point — it either returns every field or
     * throws, so there is no partial boot to degrade into.
     * @param {string} aba
     * @param {string} clientId The member being impersonated.
     * @returns {Promise<{ apiKey, identity, featureFlags, sessionId }>}
     */
    async openSsoSession(aba, clientId) {
        // Deliberately NOT stored on `this.context` like the older fetchers do:
        // the bundle carries the FI's apiKey, and nothing reads that field.
        const response = await this.API.requestSsoSession(aba || this.aba, clientId);
        return response?.data;
    }

    /**
     * Report one SSO session lifecycle event. Fire-and-forget by design: losing
     * an event must never interrupt what the admin was doing, and the
     * authoritative "a session was opened" row is written by openSsoSession.
     * @param {string} aba
     * @param {string} clientId
     * @param {'warning_shown'|'warning_cancelled'|'session_closed'} event
     * @param {string} [sessionId]
     */
    async reportSsoEvent(aba, clientId, event, sessionId) {
        try {
            await this.API.requestSsoEvent(aba || this.aba, clientId, { event, session_id: sessionId });
        } catch (err) {
            console.error(`Open SSO: failed to report ${event}`, err);
        }
    }

    /**
     * Report an SSO event from a page that is being torn down (tab close), where
     * a normal request would be cancelled mid-flight.
     *
     * NOT navigator.sendBeacon: this API authenticates with a `token` HEADER and
     * sendBeacon cannot set headers, so a beacon would arrive unauthenticated
     * and be refused. `fetch` with `keepalive: true` is the equivalent that
     * survives unload AND carries headers. Synchronous by necessity — nothing
     * awaits this, the browser is going away.
     * @param {string} aba
     * @param {string} clientId
     * @param {'session_closed'} event
     * @param {string} [sessionId]
     */
    reportSsoEventOnUnload(aba, clientId, event, sessionId) {
        try {
            const token = getAuthCookie();
            if (!token || token === 'expired') return;
            fetch(this.API.createSsoEventsUrl(aba || this.aba, clientId), {
                method: 'POST',
                keepalive: true,
                headers: { 'Content-Type': 'application/json', token },
                body: JSON.stringify({ event, session_id: sessionId }),
            }).catch(() => {});
        } catch {
            // A lost close event is not worth breaking unload over.
        }
    }

    async fetchUsers(filters = {}) {
        try {
            const response = await this.API.requestUsers(this.aba, filters);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchUsers", error);
            return {};
        }
    }

    async fetchAdminUsers(aba, {page = 1, pageSize = 10} = {}) {
        try {
            const response = await this.API.requestAdminUsers(aba || this.aba, {page, pageSize});
            this.context = response;
            const data = response?.data;
            return {
                items: data?.items ?? data?.users ?? [],
                pageinfo: data?.pageinfo ?? null
            };
        } catch (error) {
            console.error("Error in fetchAdminUsers", error);
            return {items: [], pageinfo: null};
        }
    }

    /**
     * Ledger rows for the current FI.
     *
     * Rethrows rather than answering `{}` on failure — same shape as
     * `removeUserAccess` and the other mutating calls below. An empty payload
     * reaches every caller as `items: []`, which is indistinguishable from a
     * member who genuinely has no transactions: the table then renders "No
     * transactions yet." for a failed fetch.
     *
     * This is a behaviour change for every screen that lists ledger rows, so
     * all three callers were updated with it: the user page distinguishes the
     * failure in its empty state and offers a retry, `ledger.controller.js`
     * clears the rows and flips `loading` (without its catch the rejection
     * escaped `initialize` and the Page spun forever), and Reports clears the
     * slice and reports the failure rather than leaving stale rows under the
     * new filters. Anything added later owes the same: a `catch` that logs and
     * nothing else is now a silent stale table.
     *
     * @param {Object} [filters] see `API.requestLedger`
     * @returns {Promise<Object>} the paginated `{ items, pageinfo }` payload
     * @throws whatever axios threw
     */
    async fetchLedger(filters = {}) {
        try {
            const response = await this.API.requestLedger(this.aba, filters);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchLedger", error);
            throw error;
        }
    }

    async fetchEnrollmentFunnel(filters = {}) {
        try {
            const response = await this.API.requestEnrollmentFunnel(this.aba, filters);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchEnrollmentFunnel", error);
            // Unlike the neighbouring fetchers this RETHROWS: the funnel UI
            // renders an explicit error-with-retry state and must be able to
            // tell "failed" apart from "empty".
            throw error;
        }
    }

    /**
     * Server-side Reports export (KAN-548). Resolves to one of:
     *  - `{ kind: 'file', blob, fileName, rowCount }` — save it (downloadBlob)
     *  - `{ kind: 'empty' }` — no row matched; warn, never download
     *  - `{ kind: 'job', jobId, rowCount }` — poll `fetchReportExportJob`
     * Rejects with the axios error for 4xx/5xx so the caller can show the
     * service's `{ error }` (see utils/exportResponse.js#mapExportError).
     *
     * @param {{ tab: string, scope: string, filters: Object, sort?: Object, page?: { page: number, pageSize: number } }} body
     */
    async exportReport(body) {
        const response = await this.API.requestReportsExport(this.aba, body);
        this.context = response;
        return mapExportResponse(response);
    }

    /**
     * Poll an asynchronous export. A permanent 4xx (404 not this admin's job
     * or gone, 403 scope revoked) resolves to `{ status: 'failed', error }`
     * so the poller stops; a transient failure (network, 429, 5xx) resolves
     * to `null` so the poller retries on its next tick.
     * @param {string} jobId
     * @returns {Promise<Object|null>} `{ job_id, status, row_count, download_url?, expires_at?, error? }`
     */
    async fetchReportExportJob(jobId) {
        try {
            const response = await this.API.requestReportsExportJob(this.aba, jobId);
            this.context = response;
            return response?.data ?? null;
        } catch (error) {
            console.error("Error in fetchReportExportJob", error);
            return mapExportJobError(error);
        }
    }

    async updateFI(aba, payload) {
        try {
            const response = await this.API.requestUpdateFI(aba || this.aba, payload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in updateFI", error);
            return {};
        }
    }

    async fetchYTDReport(aba) {
        try {
            const response = await this.API.requestYTDReport(aba || this.aba);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchYTDReport", error);
            return {};
        }
    }

    async fetchConsumerUser(clientId) {
        try {
            const response = await this.API.requestConsumerUser(clientId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchConsumerUser", error);
            return {};
        }
    }

    async fetchReconciliation() {
        try {
            const response = await this.API.requestReconciliation(this.aba);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchReconciliation", error);
            return {};
        }
    }

    async fetchTreasuryFunding() {
        try {
            const response = await this.API.requestTreasuryFunding(this.aba);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchTreasuryFunding", error);
            return {};
        }
    }

    async fetchTreasuryKPIs(startDate, endDate, timeFrame) {
        try {
            const response = await this.API.requestTreasuryKPIs(this.aba, startDate, endDate, timeFrame);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchTreasuryKPIs", error);
            return {};
        }
    }

    async fetchTreasuryReceivables(startDate, endDate) {
        try {
            const response = await this.API.requestTreasuryReceivables(this.aba, startDate, endDate);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchTreasuryReceivables", error);
            return {};
        }
    }

    async fetchTreasuryActivities(startDate, endDate) {
        try {
            const response = await this.API.requestTreasuryActivities(this.aba, startDate, endDate);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchTreasuryActivities", error);
            return {};
        }
    }

    async fetchTreasuryCashflow(startDate, endDate) {
        try {
            const response = await this.API.requestTreasuryCashflow(this.aba, startDate, endDate);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchTreasuryCashflow", error);
            return {};
        }
    }

    /**
     * Fetches a slice of the Advance GL data for the Cashflow Management
     * screen. `params.section` selects which slice (account, funding,
     * kpis, cashflow, aging) so the screen can call this fetcher in
     * parallel for each card. Returns the real API slice; `null` on error
     * so the card renders its empty state instead of stale/mock data.
     */
    async fetchAdvanceGL({section, start, end, grouping, timeFrame} = {}) {
        try {
            const response = await this.API.requestAdvanceGL(this.aba, {
                section,
                start,
                end,
                grouping,
                timeFrame
            });
            this.context = response;
            return response?.data ?? null;
        } catch (error) {
            console.error("Error in fetchAdvanceGL", error);
            return null;
        }
    }

    /**
     * Same shape as {@link fetchAdvanceGL} but for the Fee GL slice.
     */
    async fetchFeeGL({section, start, end, grouping, timeFrame} = {}) {
        try {
            const response = await this.API.requestFeeGL(this.aba, {
                section,
                start,
                end,
                grouping,
                timeFrame
            });
            this.context = response;
            return response?.data ?? null;
        } catch (error) {
            console.error("Error in fetchFeeGL", error);
            return null;
        }
    }

    /**
     * Returns one page of transactions for the active GL tab. Mirrors the
     * support-ticket pagination contract: `{ items, pageinfo: { page,
     * perPage, hasNext, hasPrev, count } }`. Callers REPLACE their list
     * with `items` rather than appending. On error returns an empty page
     * so the table shows its empty state rather than mock rows.
     */
    async fetchTransactionHistory({glType, page = 1, pageSize = DEFAULT_PAGE_SIZE, start, end} = {}) {
        const emptyPage = {
            items: [],
            pageinfo: {
                page,
                perPage: pageSize,
                hasNext: false,
                hasPrev: page > 1,
                count: 0
            }
        };
        try {
            const params = {glType, page, pageSize};
            if (start) params.start = start;
            if (end) params.end = end;
            const response = await this.API.requestTransactionHistory(this.aba, params);
            this.context = response;
            return response?.data ?? emptyPage;
        } catch (error) {
            console.error("Error in fetchTransactionHistory", error);
            return emptyPage;
        }
    }

    /**
     * Dashboard analytics tabs. Each returns the real per-tab payload from
     * the service (`/api/dashboard/*`) or `null` on error so the tab can
     * render its empty state instead of static fixtures.
     */
    async fetchDashboardEnrollment({start, end, grouping, timeFrame, signal} = {}) {
        try {
            const response = await this.API.requestDashboardEnrollment(
                this.aba,
                {
                    start,
                    end,
                    grouping,
                    timeFrame
                },
                signal
            );
            this.context = response;
            return response?.data ?? null;
        } catch (error) {
            if (isCanceledError(error)) throw error;
            console.error("Error in fetchDashboardEnrollment", error);
            return null;
        }
    }

    async fetchDashboardUserSummary({start, end, page = 1, pageSize = USER_SUMMARY_PAGE_SIZE} = {}) {
        try {
            const response = await this.API.requestDashboardUserSummary(this.aba, {
                start,
                end,
                page,
                pageSize
            });
            this.context = response;
            return response?.data ?? null;
        } catch (error) {
            console.error("Error in fetchDashboardUserSummary", error);
            return null;
        }
    }

    async fetchDashboardTransactionSummary({start, end, page = 1, pageSize = USER_SUMMARY_PAGE_SIZE} = {}) {
        try {
            const response = await this.API.requestDashboardTransactionSummary(this.aba, {
                start,
                end,
                page,
                pageSize
            });
            this.context = response;
            return response?.data ?? null;
        } catch (error) {
            console.error("Error in fetchDashboardTransactionSummary", error);
            return null;
        }
    }

    async fetchDashboardSessionEvents({start, end} = {}) {
        try {
            const response = await this.API.requestDashboardSessionEvents(this.aba, {
                start,
                end
            });
            this.context = response;
            return response?.data ?? null;
        } catch (error) {
            console.error("Error in fetchDashboardSessionEvents", error);
            return null;
        }
    }

    async fetchDashboardFunnel({signal} = {}) {
        try {
            const response = await this.API.requestDashboardFunnel(this.aba, signal);
            this.context = response;
            return response?.data ?? null;
        } catch (error) {
            if (isCanceledError(error)) throw error;
            console.error("Error in fetchDashboardFunnel", error);
            return null;
        }
    }

    async fetchDashboardCohortUsers({metric, start, end} = {}) {
        try {
            const response = await this.API.requestDashboardCohortUsers(this.aba, {
                metric,
                start,
                end
            });
            this.context = response;
            return response?.data ?? null;
        } catch (error) {
            console.error("Error in fetchDashboardCohortUsers", error);
            return null;
        }
    }

    async fetchDashboardUsage({start, end, grouping, timeFrame, signal} = {}) {
        try {
            const response = await this.API.requestDashboardUsage(
                this.aba,
                {
                    start,
                    end,
                    grouping,
                    timeFrame
                },
                signal
            );
            this.context = response;
            return response?.data ?? null;
        } catch (error) {
            if (isCanceledError(error)) throw error;
            console.error("Error in fetchDashboardUsage", error);
            return null;
        }
    }

    async fetchDashboardMonetization({start, end, grouping, timeFrame, signal} = {}) {
        try {
            const response = await this.API.requestDashboardMonetization(
                this.aba,
                {
                    start,
                    end,
                    grouping,
                    timeFrame
                },
                signal
            );
            this.context = response;
            return response?.data ?? null;
        } catch (error) {
            if (isCanceledError(error)) throw error;
            console.error("Error in fetchDashboardMonetization", error);
            return null;
        }
    }

    async removeUserAccess(userId) {
        try {
            const response = await this.API.requestRemoveUserAccess(this.aba, userId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in removeUserAccess", error);
            throw error;
        }
    }

    async changeUserRole(userId, roleId) {
        try {
            const response = await this.API.requestChangeUserRole(this.aba, userId, roleId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in changeUserRole", error);
            throw error;
        }
    }

    async sendInvitation(payload) {
        try {
            // Send email, the selected role id, and aba. The server resolves the
            // role from its own catalog and enforces who may assign it (only
            // owners can invite owners).
            const {email, roleId} = payload;
            if (!email) {
                throw new Error("Email is required for sending invitation");
            }
            if (!roleId) {
                throw new Error("Role is required for sending invitation");
            }

            const payloadWithAba = {email, role_id: roleId, aba: this.aba};
            const response = await this.API.requestSendInvitation(payloadWithAba);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in sendInvitation", error);
            throw error;
        }
    }

    async fetchRoles() {
        try {
            const response = await this.API.requestListRoles(this.aba);
            return response?.data?.roles ?? [];
        } catch (error) {
            console.error('Error in fetchRoles', error);
            return [];
        }
    }

    async fetchScopesCatalog() {
        try {
            const response = await this.API.requestScopesCatalog(this.aba);
            return response?.data?.scopes ?? [];
        } catch (error) {
            console.error('Error in fetchScopesCatalog', error);
            return [];
        }
    }

    async createRole(name, cloneFromRoleId, description) {
        try {
            const response = await this.API.requestCreateRole(this.aba, { name, description, clone_from_role_id: cloneFromRoleId });
            return response?.data;
        } catch (error) {
            console.error('Error in createRole', error);
            throw error;
        }
    }

    async renameRole(roleId, name) {
        try {
            const response = await this.API.requestUpdateRole(this.aba, roleId, { name });
            return response?.data;
        } catch (error) {
            console.error('Error in renameRole', error);
            throw error;
        }
    }

    async updateRole(roleId, name, description) {
        try {
            const response = await this.API.requestUpdateRole(this.aba, roleId, { name, description });
            return response?.data;
        } catch (error) {
            console.error('Error in updateRole', error);
            throw error;
        }
    }

    async deleteRole(roleId) {
        try {
            const response = await this.API.requestDeleteRole(this.aba, roleId);
            return response?.data;
        } catch (error) {
            console.error('Error in deleteRole', error);
            throw error;
        }
    }

    async setRoleScopes(roleId, scopeIds) {
        try {
            const response = await this.API.requestSetRoleScopes(this.aba, roleId, scopeIds);
            return response?.data;
        } catch (error) {
            console.error('Error in setRoleScopes', error);
            throw error;
        }
    }

    async setDefaultRole(roleId) {
        try {
            const response = await this.API.requestSetDefaultRole(this.aba, roleId);
            return response?.data;
        } catch (error) {
            console.error('Error in setDefaultRole', error);
            throw error;
        }
    }

    async validateInvitation(token) {
        try {
            const response = await this.API.requestValidateInvitation(token);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in validateInvitation", error);
            throw error;
        }
    }

    async validatePasswordReset(token) {
        try {
            const response = await this.API.requestValidatePasswordReset(token);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in validatePasswordReset", error);
            throw error;
        }
    }

    async updatePassword(payload) {
        try {
            const response = await this.API.requestUpdatePassword(payload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in updatePassword", error);
            throw error;
        }
    }

    async registerUser(payload) {
        try {
            // Transform payload to match API expected format
            const {firstName, lastName, email, password, phoneNumber} = payload;

            const registerPayload = {
                name: `${firstName} ${lastName}`.trim(),
                email,
                password
            };

            if (phoneNumber) {
                registerPayload.phone = phoneNumber;
            }

            const response = await this.API.requestRegister(registerPayload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in registerUser", error);
            throw error;
        }
    }

    async updateAccount(payload) {
        try {
            const response = await this.API.requestUpdateAccount(payload);
            this.context = response;

            // Update local user data if successful
            if (response?.data) {
                this.user = {...this.user, ...response.data};
            }

            return response?.data;
        } catch (error) {
            console.error("Error in updateAccount", error);
            throw error;
        }
    }

    async sendPasswordReset(email) {
        try {
            if (!email) {
                throw new Error("Email is required for password reset");
            }

            const payload = {email};
            const response = await this.API.requestSendPasswordReset(this.aba, payload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in sendPasswordReset", error);
            throw error;
        }
    }

    async fetchBillingKPIs(startDate, endDate) {
        try {
            const response = await this.API.requestBillingKPIs(this.aba, startDate, endDate);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchBillingKPIs", error);
            return {};
        }
    }

    async fetchBillingBillableUsers(params = {}) {
        try {
            const response = await this.API.requestAnalyticsBillableUsers(this.aba, params);
            this.context = response;
            return response?.data?.data ?? {};
        } catch (error) {
            console.error("Error in fetchBillingBillableUsers", error);
            return {};
        }
    }

    /**
     * Athena adoption metrics by interval (daily / weekly / monthly).
     * Used client-side for dashboard charts — one interval per request.
     */
    async fetchAnalyticsMetrics({timeFrame, dateRange, start, end, interval} = {}) {
        try {
            const periodParams = timeFrame ? buildAnalyticsPeriodParams(timeFrame, dateRange) : buildAnalyticsPeriodParams("custom", null, {forceCustom: true, start, end});

            const response = await this.API.requestAnalyticsMetrics(this.aba, {
                ...periodParams,
                interval
            });
            this.context = response;
            return response?.data ?? null;
        } catch (error) {
            console.error("Error in fetchAnalyticsMetrics", error);
            return null;
        }
    }

    async fetchBillingStatements(startDate, endDate) {
        try {
            const response = await this.API.requestBillingStatements(this.aba, startDate, endDate);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchBillingStatements", error);
            return [];
        }
    }

    async exportBillingStatements(statementIds) {
        try {
            const payload = {statementIds};
            const response = await this.API.requestExportBillingStatements(this.aba, payload);

            // Create a download link
            if (response?.data) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `billing_statements_${new Date().toISOString().split("T")[0]}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }

            return response?.data;
        } catch (error) {
            console.error("Error in exportBillingStatements", error);
            throw error;
        }
    }

    async fetchSyncMonitor(status = null, limit = 50) {
        try {
            const response = await this.API.requestSyncMonitor(this.aba, status, limit);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchSyncMonitor", error);
            return {syncs: []};
        }
    }

    async sendSyncNotification(clientId, notificationType, linkTokenId) {
        try {
            const payload = {
                client_id: clientId,
                notification_type: notificationType,
                link_token_id: linkTokenId
            };
            const response = await this.API.requestSendSyncNotification(this.aba, payload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in sendSyncNotification", error);
            throw error;
        }
    }

    async resolveTroubleTicket(ticketId, sendNotification = false) {
        try {
            const payload = {
                send_notification: sendNotification,
                aba: this.aba
            };
            const response = await this.API.requestResolveTroubleTicket(ticketId, payload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in resolveTroubleTicket", error);
            throw error;
        }
    }

    async fetchPlatforms(provider = null, search = null) {
        try {
            const response = await this.API.requestPlatforms(this.aba, provider, search);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchPlatforms", error);
            return {platforms: []};
        }
    }

    async fetchPlatformOutages(platformId = null, provider = null, status = "active") {
        try {
            const response = await this.API.requestPlatformOutages(this.aba, platformId, provider, status);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchPlatformOutages", error);
            return {outages: []};
        }
    }

    async createPlatformOutage(payload) {
        try {
            const response = await this.API.requestCreatePlatformOutage(this.aba, payload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in createPlatformOutage", error);
            throw error;
        }
    }

    async updatePlatformOutage(outageId, payload) {
        try {
            const response = await this.API.requestUpdatePlatformOutage(this.aba, outageId, payload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in updatePlatformOutage", error);
            throw error;
        }
    }

    async resolvePlatformOutage(outageId) {
        try {
            const response = await this.API.requestResolvePlatformOutage(this.aba, outageId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in resolvePlatformOutage", error);
            throw error;
        }
    }

    /**
     * Per-user sync detail report. THROWS on failure — a `null` return used to
     * be indistinguishable from "no sync row", which is exactly the ambiguity
     * the sync-status screens must not have (a failed load renders an error
     * with Retry; "no sync" renders as the NONE state).
     */
    /**
     * `GET /api/admin/users/:aba/:client_id/sync-status`.
     *
     * Contract (clockout-service `routes/admin/userSyncStatus.js`, on main and
     * on KAN-557): a member with NO payroll sync row is a 200 carrying a
     * `rollup_status: NONE` shell — never a 404 — so any rejection here is a
     * genuine failure and is rethrown. The request itself is bounded
     * (`SYNC_STATUS_TIMEOUT_MS` in api.js); `error.reason` classifies it for
     * the screens as `'timeout'` or `'load_failed'`, so no caller races its
     * own timer or leaves the request running.
     */
    async fetchUserSyncStatus(clientId) {
        try {
            const response = await this.API.requestUserSyncStatus(this.aba, clientId);
            this.context = response;
            return response?.data;
        } catch (error) {
            // ETIMEDOUT only (api.js sets clarifyTimeoutError): ECONNABORTED is
            // also a plain abort, which must not read as "took too long".
            error.reason = error?.code === "ETIMEDOUT" ? "timeout" : "load_failed";
            console.error("Error in fetchUserSyncStatus", error);
            throw error;
        }
    }

    /**
     * One member's onboarding journey (nine fixed stages plus the conditional
     * payroll-error rows). Backed by an Athena scan, so it is fetched on its
     * own — never bundled with the detail payload — and it rethrows instead of
     * swallowing: the caller needs the status code to tell "no such member"
     * (404) apart from "the lake is down", and the two read very differently
     * to a support agent.
     */
    async fetchUserFunnel(clientId) {
        try {
            const response = await this.API.requestUserFunnel(this.aba, clientId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchUserFunnel", error);
            throw error;
        }
    }

    /**
     * The payroll-link failures behind the funnel's error row. A second Athena
     * scan, so it is fetched only when an agent opens that row — never with
     * the page. Rethrows for the same reason `fetchUserFunnel` does: the
     * caller needs the status code to say something useful.
     */
    async fetchUserLinkAttempts(clientId) {
        try {
            const response = await this.API.requestUserLinkAttempts(this.aba, clientId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchUserLinkAttempts", error);
            throw error;
        }
    }

    /**
     * Payroll-connection summary for one member: one entry per connection with
     * platform, state, last successful connection and event count. No events.
     *
     * Rethrows, unlike `fetchUserSyncStatus` above, which swallows to `null`.
     * The panel renders a real in-card error with a Retry, so it needs to know
     * the difference between "failed" and "this member has no connections".
     */
    async fetchUserPayrollConnections(clientId) {
        try {
            const response = await this.API.requestUserPayrollConnections(this.aba, clientId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchUserPayrollConnections', error);
            throw error;
        }
    }

    /**
     * One page of the connection record for a single connection, newest first.
     *
     * `connectionKey` is the summary's `connection_key` — a uuid `account_id`,
     * or the literal "unassigned". Rethrows for the same reason as above.
     */
    async fetchPayrollConnectionEvents(clientId, connectionKey, query = {}) {
        try {
            const response = await this.API.requestUserPayrollConnectionEvents(
                this.aba,
                clientId,
                connectionKey,
                query
            );
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchPayrollConnectionEvents', error);
            throw error;
        }
    }

    /**
     * Every sync row of one member with the data each dimension stored
     * (KAN-678). Rethrows, like the payroll-connection reads, so the card can
     * tell "failed" apart from "never synced".
     */
    async fetchUserSyncHistory(clientId) {
        try {
            const response = await this.API.requestUserSyncHistory(this.aba, clientId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error('Error in fetchUserSyncHistory', error);
            throw error;
        }
    }

    async restrictUser(clientId, active = false) {
        try {
            const response = await this.API.requestUserRestrict(this.aba, clientId, active);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in restrictUser", error);
            throw error;
        }
    }

    async deleteUserLatestSync(clientId) {
        try {
            const response = await this.API.requestUserDeleteLatestSync(this.aba, clientId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in deleteUserLatestSync", error);
            throw error;
        }
    }

    async markUserLatestSyncComplete(clientId) {
        try {
            const response = await this.API.requestUserMarkLatestSyncComplete(this.aba, clientId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in markUserLatestSyncComplete", error);
            throw error;
        }
    }

    async reconcileUserAccount(clientId) {
        try {
            const response = await this.API.requestUserReconcileAccount(this.aba, clientId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in reconcileUserAccount", error);
            throw error;
        }
    }

    async fetchTickets(filters = {}) {
        try {
            const response = await this.API.requestTickets(this.aba, filters);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchTickets", error);
            throw error;
        }
    }

    /**
     * One page of the issue-tracker mirror
     * (`GET /api/admin/jira/:aba/issues`).
     *
     * With `status`, this is one board COLUMN's page — the board pages each
     * status independently as it is scrolled. Without it, one page of the table.
     *
     * Rethrows: unlike a list that can degrade to "no rows", an unreachable
     * endpoint here has to reach the screen, because an empty view means
     * "nothing matches this filter" and must never be how a broken integration
     * looks.
     *
     * @param {{ status?: string, page?: number, perPage?: number }} [filters]
     * @returns {Promise<{ items: object[], pageinfo: object, sync: object }>}
     */
    async fetchJiraIssues(filters = {}) {
        try {
            const response = await this.API.requestJiraIssues(this.aba, filters);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchJiraIssues", error);
            throw error;
        }
    }

    /**
     * One issue in full (`GET /api/admin/jira/:aba/issues/:key`), including the
     * support tickets THIS FI raised against it.
     *
     * Rethrows so the detail view can show its own error rather than an empty
     * modal that looks like an issue with no fields.
     *
     * @param {string} key
     * @returns {Promise<object>}
     */
    async fetchJiraIssue(key) {
        try {
            const response = await this.API.requestJiraIssue(this.aba, key);
            this.context = response;
            return response?.data?.issue ?? null;
        } catch (error) {
            console.error("Error in fetchJiraIssue", error);
            throw error;
        }
    }

    /**
     * The issue-tracker status/stage vocabulary
     * (`GET /api/admin/jira/status-map`), fetched ONCE per session: it is one
     * contract for every FI, it changes only on deploy, and both the table and
     * the ticket sidebar render labels from it.
     *
     * Never throws — on failure the screens fall back to the labels bundled in
     * `issueStatus.constants`, and the next screen retries.
     *
     * @returns {Promise<{ statuses: object[], stages: object[] } | null>}
     */
    async fetchJiraStatusMap() {
        if (this._jiraStatusMapPromise) return this._jiraStatusMapPromise;
        this._jiraStatusMapPromise = (async () => {
            try {
                const response = await this.API.requestJiraStatusMap();
                const map = response?.data;
                if (!Array.isArray(map?.statuses)) throw new Error('jira status map payload has no statuses');
                return map;
            } catch (error) {
                console.error('Error in fetchJiraStatusMap', error);
                this._jiraStatusMapPromise = null; // let the next screen retry
                return null;
            }
        })();
        return this._jiraStatusMapPromise;
    }

    async fetchTicket(ticketId, messageQuery = {}) {
        try {
            const response = await this.API.requestTicket(this.aba, ticketId, messageQuery);
            this.context = response;
            return response.data;
        } catch (error) {
            console.error("Error in fetchTicket", error);
            throw error;
        }
    }

    /**
     * Request a presigned URL for file upload to S3
     * @param {string} fileName - Original filename
     * @param {string} mimeType - MIME type
     * @returns {Promise<{presignedUrl: string, objectKey: string, publicUrl: string}>}
     */
    async requestPresignedTicketUrl(fileName, mimeType) {
        const res = await this.API.requestPresignedTicketMedia(this.aba, {fileName, mimeType});
        const presigned = res?.data;
        if (!presigned?.presignedUrl) {
            throw new Error("Failed to get presigned URL");
        }
        return presigned;
    }

    /**
     * Upload a file directly to S3 using a presigned URL
     * @param {string} presignedUrl - Presigned URL from S3
     * @param {File} file - File to upload
     * @param {string} mimeType - MIME type for Content-Type header
     * @returns {Promise<void>}
     */
    async uploadFileToS3(presignedUrl, file, mimeType) {
        const controller = new AbortController();
        const timeoutMs = 60 * 1000; // 60 seconds
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        try {
            const response = await fetch(presignedUrl, {
                method: "PUT",
                body: file,
                headers: {"Content-Type": mimeType},
                signal: controller.signal
            });
            if (!response.ok) {
                throw new Error(`S3 upload failed: ${response.statusText}`);
            }
        } catch (error) {
            if (error.name === "AbortError") {
                throw new Error(`Upload timeout: took longer than ${timeoutMs / 1000}s to upload file`);
            }
            throw error;
        } finally {
            clearTimeout(timeoutId);
        }
    }

    /**
     * Admin notification feed. Returns `{ items, pageinfo: { page, perPage,
     * hasNext, hasPrev, count } }` in the KAN-509 camelCase row shape.
     * Throws so the screen can surface the failure.
     * @param {{page?: number, perPage?: number, type?: string, unread?: boolean, start?: string, end?: string}} filters
     */
    async fetchNotifications(filters = {}) {
        try {
            const response = await this.API.requestNotifications(this.aba, filters);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchNotifications", error);
            throw error;
        }
    }

    /**
     * Unread count for the header bell. Never throws — the bell must not
     * take down the shell; returns null when the request fails.
     * @returns {Promise<number|null>}
     */
    async fetchNotificationsUnreadCount() {
        try {
            if (!this.aba) return null;
            const response = await this.API.requestNotificationsUnreadCount(this.aba);
            const count = Number(response?.data?.count);
            return Number.isFinite(count) ? count : null;
        } catch (error) {
            console.error("Error in fetchNotificationsUnreadCount", error);
            return null;
        }
    }

    /**
     * Mark the given notifications read.
     * @param {string[]} ids
     * @returns {Promise<{updated: number}|undefined>}
     */
    async markNotificationsRead(ids) {
        try {
            const response = await this.API.requestMarkNotificationsRead(this.aba, { ids });
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in markNotificationsRead", error);
            throw error;
        }
    }

    async createTicket(payload, files = []) {
        try {
            const response = await this.API.requestCreateTicket(this.aba, {...payload, files});
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in createTicket", error);
            throw error;
        }
    }

    async createTicketFromEvent(payload) {
        try {
            const response = await this.API.requestCreateTicketFromEvent(this.aba, payload);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in createTicketFromEvent", error);
            throw error;
        }
    }

    async replyToTicket(ticketId, body, files = []) {
        try {
            const response = await this.API.requestCreateTicketMessage(this.aba, ticketId, {body, files});
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in replyToTicket", error);
            throw error;
        }
    }

    async markTicketRead(ticketId) {
        try {
            const response = await this.API.requestMarkTicketRead(this.aba, ticketId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in markTicketRead", error);
            throw error;
        }
    }

    async fetchReviewsSummary(params = {}) {
        try {
            const response = await this.API.requestReviewsSummary(this.aba, params);
            return response?.data;
        } catch (error) {
            console.error("Error in fetchReviewsSummary", error);
            return {};
        }
    }

    async fetchReviews(params = {}) {
        try {
            const response = await this.API.requestReviews(this.aba, params);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchReviews", error);
            return {};
        }
    }

    async fetchReviewDetail(eventId) {
        try {
            const response = await this.API.requestReviewDetail(this.aba, eventId);
            return response?.data;
        } catch (error) {
            console.error("Error in fetchReviewDetail", error);
            throw error;
        }
    }

    /**
     * Kick off a headless intake simulation (Uploads Simulator). Responds fast
     * with `{simulation_id, poll_url}` — the heavy AI work happens behind the
     * poll endpoint. RETHROWS after logging (mirrors the `removeUserAccess`
     * precedent) because the page must distinguish error kinds (403 access vs
     * 413 size vs 429 concurrency vs cancellation, …).
     *
     * @param {{mode: string, sor: object, files: File[], signal?: AbortSignal, onUploadProgress?: Function}} options
     */
    async simulateIntake({mode, sor, files, signal, onUploadProgress}) {
        try {
            const response = await this.API.requestSimulateIntake(this.aba, {mode, sor, files}, {signal, onUploadProgress});
            this.context = response;
            return response?.data;
        } catch (error) {
            if (!isCanceledError(error)) console.error("Error in simulateIntake", error);
            throw error;
        }
    }

    /**
     * Poll one simulation run. RETHROWS after logging — a 404 means the run
     * expired (15-minute TTL) and the page surfaces that distinctly.
     *
     * @param {string} simulationId
     * @param {{signal?: AbortSignal}} [options]
     */
    async fetchSimulationRun(simulationId, {signal} = {}) {
        try {
            const response = await this.API.requestSimulationRun(this.aba, simulationId, {signal});
            this.context = response;
            return response?.data;
        } catch (error) {
            if (!isCanceledError(error)) console.error("Error in fetchSimulationRun", error);
            throw error;
        }
    }

    /**
     * Composer prefill for one mode (`{mode, notes, sor}`). Swallows errors →
     * null (standard fetcher shape): a missing preset just means the composer
     * starts blank.
     *
     * @param {string} mode
     */
    async fetchSimulationPreset(mode) {
        try {
            const response = await this.API.requestSimulationPreset(this.aba, mode);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchSimulationPreset", error);
            return null;
        }
    }

    /**
     * A real member's SOR for the composer (`{sor, meta}`; meta carries
     * `notes[]` caveats). RETHROWS after logging (mirrors `simulateIntake`)
     * so the page can map 404 ("User not found for this FI") vs 403 distinctly.
     *
     * @param {string} clientId
     */
    async fetchMemberSor(clientId) {
        try {
            const response = await this.API.requestMemberSor(this.aba, clientId);
            this.context = response;
            return response?.data;
        } catch (error) {
            console.error("Error in fetchMemberSor", error);
            throw error;
        }
    }

    /**
     * Real manual-upload intake jobs for one member (Document Audit tab).
     * RETHROWS after logging — the page distinguishes 404 (member not under
     * this FI) from 403 (no access) from network failures.
     *
     * @param {string} clientId
     * @param {{signal?: AbortSignal}} [options]
     */
    async fetchIntakeJobs(clientId, {signal} = {}) {
        try {
            const response = await this.API.requestIntakeJobs(this.aba, clientId, {signal});
            this.context = response;
            return response?.data;
        } catch (error) {
            if (!isCanceledError(error)) console.error("Error in fetchIntakeJobs", error);
            throw error;
        }
    }

    /**
     * Stored document bytes for one intake job as a Blob (S3-retained jobs
     * only). RETHROWS after logging — a 404 means the bytes were never
     * retained or have expired. Because the request uses `responseType:
     * 'blob'`, the ERROR body is a Blob as well: callers must map by HTTP
     * status and never try to read `.error` from it.
     *
     * @param {string} clientId
     * @param {string} jobId
     * @param {{signal?: AbortSignal}} [options]
     * @returns {Promise<Blob>}
     */
    async fetchIntakeJobContent(clientId, jobId, {signal} = {}) {
        try {
            const response = await this.API.requestIntakeJobContent(this.aba, clientId, jobId, {signal});
            this.context = response;
            return response?.data;
        } catch (error) {
            if (!isCanceledError(error)) console.error("Error in fetchIntakeJobContent", error);
            throw error;
        }
    }

    async fetchAccessList(aba, action = null) {
        try {
            const response = await this.API.requestAccessList(aba || this.aba, action);
            return response?.data?.data || [];
        } catch (error) {
            console.error('Error in fetchAccessList', error);
            return [];
        }
    }

    async createAccessRule(aba, payload) {
        try {
            const response = await this.API.requestCreateAccessRule(aba || this.aba, payload);
            return response?.data?.data;
        } catch (error) {
            console.error('Error in createAccessRule', error);
            throw error;
        }
    }

    async deleteAccessRule(aba, ruleId) {
        try {
            const response = await this.API.requestDeleteAccessRule(aba || this.aba, ruleId);
            return response?.data?.success;
        } catch (error) {
            console.error('Error in deleteAccessRule', error);
            throw error;
        }
    }

    async bulkUploadAccessList(aba, formData) {
        try {
            const response = await this.API.requestBulkUploadAccessList(aba || this.aba, formData);
            return response?.data;
        } catch (error) {
            console.error('Error in bulkUploadAccessList', error);
            throw error;
        }
    }

    async getAccessListBulkStatus(aba, batchId) {
        try {
            const response = await this.API.requestAccessListBulkStatus(aba || this.aba, batchId);
            return response?.data?.data;
        } catch (error) {
            console.error('Error in getAccessListBulkStatus', error);
            throw error;
        }
    }

    // --- Free-advance promo groups ---------------------------------------
    // Response envelopes here are `super.end(res, CODE, payload)` on the
    // service side — the payload IS the body (`{groups}`, `{group}`,
    // `{members}`, `{member}`), not the older `{success, data}` wrapper
    // access-list's routes use.

    // Rethrows, unlike fetchAccessList's swallow-and-return-[] — the caller
    // (MemberPromosController) needs the real status code. A 403 here is the
    // ONLY signal that the pilot feature scope is actually enabled for this
    // FI's instance: `hasScope()` alone can't tell, because the Owner role
    // is granted this scope unconditionally the moment it exists in the
    // catalog (see clockout-service sql/V12), regardless of its live
    // `enabled` column — which has no other client-visible mirror.
    async fetchFreeAdvancePromos(aba) {
        const response = await this.API.requestFreeAdvancePromos(aba || this.aba);
        return response?.data?.groups || [];
    }

    async createFreeAdvancePromo(aba, payload) {
        try {
            const response = await this.API.requestCreateFreeAdvancePromo(aba || this.aba, payload);
            return response?.data?.group;
        } catch (error) {
            console.error('Error in createFreeAdvancePromo', error);
            throw error;
        }
    }

    async updateFreeAdvancePromo(aba, promoId, payload) {
        try {
            const response = await this.API.requestUpdateFreeAdvancePromo(aba || this.aba, promoId, payload);
            return response?.data?.group;
        } catch (error) {
            console.error('Error in updateFreeAdvancePromo', error);
            throw error;
        }
    }

    async deleteFreeAdvancePromo(aba, promoId) {
        try {
            const response = await this.API.requestDeleteFreeAdvancePromo(aba || this.aba, promoId);
            return response?.data;
        } catch (error) {
            console.error('Error in deleteFreeAdvancePromo', error);
            throw error;
        }
    }

    async fetchFreeAdvancePromoMembers(aba, promoId, state = null) {
        try {
            const response = await this.API.requestFreeAdvancePromoMembers(aba || this.aba, promoId, state);
            return response?.data?.members || [];
        } catch (error) {
            console.error('Error in fetchFreeAdvancePromoMembers', error);
            return [];
        }
    }

    async addFreeAdvancePromoMember(aba, promoId, memberId) {
        try {
            const response = await this.API.requestAddFreeAdvancePromoMember(aba || this.aba, promoId, memberId);
            return response?.data?.member;
        } catch (error) {
            console.error('Error in addFreeAdvancePromoMember', error);
            throw error;
        }
    }

    /**
     * Bulk enroll. Unlike addFreeAdvancePromoMember, a rejected member is not
     * an exception — the response carries `conflicts` alongside the members
     * that landed, so callers must read both.
     * @returns {Promise<{members: Object[], created: number, reactivated: number, conflicts: Object[]}>}
     */
    async addFreeAdvancePromoMembers(aba, promoId, memberIds) {
        try {
            const response = await this.API.requestAddFreeAdvancePromoMembersBatch(aba || this.aba, promoId, memberIds);
            const data = response?.data || {};
            return {
                members: data.members || [],
                created: data.created || 0,
                reactivated: data.reactivated || 0,
                conflicts: data.conflicts || []
            };
        } catch (error) {
            console.error('Error in addFreeAdvancePromoMembers', error);
            throw error;
        }
    }

    /**
     * Bulk remove. Idempotent: ids that had no active membership come back in
     * `not_found` rather than throwing the way the single-member DELETE does.
     * @returns {Promise<{members: Object[], removed: number, not_found: string[]}>}
     */
    async removeFreeAdvancePromoMembers(aba, promoId, memberIds) {
        try {
            const response = await this.API.requestRemoveFreeAdvancePromoMembersBatch(aba || this.aba, promoId, memberIds);
            const data = response?.data || {};
            return {
                members: data.members || [],
                removed: data.removed || 0,
                not_found: data.not_found || []
            };
        } catch (error) {
            console.error('Error in removeFreeAdvancePromoMembers', error);
            throw error;
        }
    }

    async removeFreeAdvancePromoMember(aba, promoId, memberId) {
        try {
            const response = await this.API.requestRemoveFreeAdvancePromoMember(aba || this.aba, promoId, memberId);
            return response?.data?.member;
        } catch (error) {
            console.error('Error in removeFreeAdvancePromoMember', error);
            throw error;
        }
    }
}

export default DataController;
